import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { decodeData } from '../../utils/encodeAndEncode';
import { getPublicPostDetails } from '../../services/feedServices';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';

const DefaultProfile = '/images/profile/default-profile.png';

const PublicPostDetails = () => {
  const navigate = useNavigate();
  const { encodedId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullText, setShowFullText] = useState(false);

  const decoded = decodeData(encodedId);
  const postId = decoded?.id;

  useEffect(() => {
    if (postId) {
      fetchPostDetails();
    } else {
      setError('Invalid post URL');
      setLoading(false);
    }
  }, [postId]);

  const fetchPostDetails = async () => {
    try {
      setLoading(true);
      const response = await getPublicPostDetails({
        post_id: postId,
        domain: window.location.origin  
      });
      
      if (response.success) {
        setPost(response.data.post);
      } else {
        setError('Post not found');
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError('Post not available');
      } else if (error.response && error.response.data && error.response.data.error_msg === 'Post not found') {
        setError('Post not available');
      } else {
        console.error('Error fetching post details:', error);
        setError('Failed to load post');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const currentUrl = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: 'Check out this post',
        text: post.description || 'Shared from our platform',
        url: currentUrl,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(currentUrl).then(() => {
        toast.success('Link copied to clipboard!');
      }).catch(() => {
        toast.error('Failed to copy link');
      });
    }
  };

  const handleLogin = () => {
    navigate('/');
  };

  const renderMedia = (media) => {
    if (!media) return null;

    return (
      <div className="mb-4">
        {media.type === 'image' ? (
          <img 
            src={media.url} 
            alt="Post media" 
            className="w-full rounded-lg object-contain max-h-[500px]"
          />
        ) : media.type === 'video' ? (
          <video 
            controls 
            className="w-full rounded-lg max-h-[500px]"
          >
            <source src={media.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-4 px-4">
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-4 px-4">
        <div className="text-center py-12">
          {error === 'Post not available' ? (
            <>
              <Icon icon="mdi:post-outline" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">Post Not Available</h3>
              <p className="text-gray-500 mb-4">This post may have been deleted or is no longer accessible.</p>
              <button 
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2"
                onClick={() => navigate('/')}
              >
                <Icon icon="mdi:home" className="w-5 h-5" />
                Go to Home
              </button>
            </>
          ) : (
            <>
              <Icon icon="mdi:alert-circle-outline" className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-500 mb-2">Error</h3>
              <p className="text-gray-500">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto py-4 px-4">
        <div className="text-center py-12">
          <Icon icon="mdi:post-outline" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-500 mb-2">Post Not Found</h3>
          <p className="text-gray-500">The post you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-4 px-4">
      {/* Logo Section */}
      <div className="text-center mb-6">
        <img 
          src={getLogoByDomainAndAlt().logo} 
          alt={getLogoByDomainAndAlt().alt}
          className="h-14 max-w-[200px] mx-auto object-contain"
        />
      </div>
      
      {/* Post Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6">
          {/* Post Header */}
          <div className="flex items-center mb-4">
            <img 
              src={post.user_avatar || DefaultProfile} 
              className="w-12 h-12 rounded-full object-cover mr-3" 
              alt={post.user_name}
            />
            <div className="flex-1">
              <h6 className="font-bold text-font-primary">{post.user_name}</h6>
              <small className="text-gray-500">
                {new Date(post.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </small>
            </div>
          </div>

          {/* Post Content */}
          {post.description && (
            <div className="mb-4">
              {(() => {
                const hasMedia = post.media_url && (post.media_type === 'image' || post.media_type === 'video');
                
                if (!hasMedia) {
                  return (
                    <p className="whitespace-pre-wrap leading-relaxed text-font-primary">
                      {post.description}
                    </p>
                  );
                }

                const shouldTruncate = post.description.length > 100;
                const displayText = showFullText ? post.description : post.description.substring(0, 100) + (shouldTruncate ? '...' : '');

                return (
                  <p className="whitespace-pre-wrap leading-relaxed text-font-primary">
                    {displayText}
                    {shouldTruncate && (
                      <button
                        className="ml-2 text-primary hover:text-primary/80 font-medium"
                        onClick={() => setShowFullText(!showFullText)}
                      >
                        {showFullText ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </p>
                );
              })()}
            </div>
          )}

          {/* Post Media */}
          {post.media_url && renderMedia({
            type: post.media_type,
            url: post.media_url
          })}

          {/* Post Stats */}
          <div className="border-t border-gray-100 pt-4 mt-4">
            <div className="flex gap-2">
              {/* Like Button */}
              <button 
                className="flex-1 py-2 px-3 border border-gray-200 rounded-lg text-gray-500 flex items-center justify-center gap-2 opacity-60 cursor-not-allowed"
                disabled
              >
                <Icon icon="mdi:heart-outline" className="w-5 h-5" />
                {post.likes_count || 0}
              </button>
              
              {/* Comment Button */}
              <button 
                className="flex-1 py-2 px-3 border border-gray-200 rounded-lg text-gray-500 flex items-center justify-center gap-2 opacity-60 cursor-not-allowed"
                disabled
              >
                <Icon icon="mdi:comment-outline" className="w-5 h-5" />
                {post.comments_count || 0}
              </button>
              
              {/* Share Button */}
              <button 
                className="flex-1 py-2 px-3 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                onClick={handleShare}
              >
                <Icon icon="mdi:share-variant" className="w-5 h-5" />
                Share
              </button>
            </div>
          </div>

          {/* Login Call-to-Action */}
          <div className="mt-4 flex items-center justify-end">
            <button 
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2 transition-colors"
              onClick={handleLogin}
            >
              <Icon icon="mdi:login" className="w-5 h-5" />
              Login
            </button>
          </div>

          {/* Share Info */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <small className="text-gray-500 flex items-center gap-2">
              <Icon icon="mdi:information-outline" className="w-4 h-4" />
              Login to like, comment, and engage with posts. Join our community to start sharing your thoughts!
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPostDetails;
